import React from "react";
import { Calendar, User, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { BannerAd } from "@/components/ads/BannerAd";

const categories = [
  "All",
  "Soccer Training",
  "Soccer Equipment",
  "Team Building",
  "Health",
  "Safety",
  "Soccer News",
];

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = React.useState("All");

  const { data: blogPosts = [], isLoading } = useBlogPosts({
    category: selectedCategory !== "All" ? selectedCategory : undefined,
  });

  return (
    <>
      <Helmet>
        <title>Youth Soccer News & Training Tips | Minor Soccer Talks</title>
        <meta
          name="description"
          content="Stay ahead in youth soccer with Minor Soccer Talks. Explore player development, coaching tips, training insights, and stories from grassroots to elite levels."
        />
        <meta
          name="keywords"
          content="youth soccer, soccer training, player development, soccer news, coaching tips, soccer tournaments"
        />
        <link rel="canonical" href={`${window.location.origin}/blogs`} />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Minor Soccer Talks – Youth Soccer News, Training Tips & Player Development Stories</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Stay updated with Minor Soccer Talks. Explore youth soccer news, coaching tips, player development insights, tournaments, and training advice for all levels.
          </p>
        </div>

      {/* Banner Ad Above Tabs */}
      <BannerAd
        id="blog-banner-ad"
        mobileUnitPath="/21849154601,423899568/Ad.Plus-Mobile-Blog-Banner"
        desktopUnitPath="/21849154601,423899568/Ad.Plus-Desktop-Blog-Banner"
      />

      {/* Category Filter */}
      <h2 className="sr-only">Filter blog posts by category</h2>
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {categories.map((category) => (
          <Button
            key={category}
            variant={selectedCategory === category ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </Button>
        ))}
      </div>

      {/* Blog Posts Grid */}
      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">Loading blog posts...</p>
        </div>
      ) : blogPosts.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            No blog posts found for the selected category.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogPosts.map((post) => (
            <Link to={`/blog/${post.slug}`}>
              <Card
                key={post.id}
                className="overflow-hidden hover:shadow-lg transition-shadow"
              >
                {post.featured_image ? (
                  <div className="aspect-video bg-muted relative">
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.nextElementSibling?.classList.remove(
                          "hidden"
                        );
                      }}
                    />
                    <div className="hidden absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                      <span className="text-2xl font-bold text-muted-foreground">
                        {post.category}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-video bg-muted relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                      <span className="text-2xl font-bold text-muted-foreground">
                        {post.category}
                      </span>
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="secondary" className="text-xs">
                      {post.category}
                    </Badge>
                    {post.read_time && (
                      <span className="text-xs text-muted-foreground">
                        {post.read_time} min read
                      </span>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold mb-2 line-clamp-2">
                    {post.title}
                  </h3>

                  <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span>{post.author_username || "Admin"}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>
                        {new Date(
                          post.published_at || post.created_at
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full group"
                    asChild
                  >
                    <Link to={`/blog/${post.slug}`} aria-label={`Read the full article about ${post.title}`}>
                      Read the full article
                      <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Coming Soon Message - only show if no posts */}
      {!isLoading && blogPosts.length === 0 && (
        <div className="text-center mt-12 p-8 bg-muted/50 rounded-lg">
          <h2 className="text-2xl font-semibold mb-4">
            More Content Coming Soon!
          </h2>
          <p className="text-muted-foreground mb-6">
            We're working on bringing you more valuable content about youth
            soccer. Stay tuned for regular updates, expert insights, and
            community stories.
          </p>
          <Button variant="outline">Subscribe for Updates</Button>
        </div>
      )}
      </div>
    </>
  );
};

export default Blog;
